<template>
    <v-sheet class="pa-2">
        <div class="mt-8" v-if="loading && !initialized">
            <box-view-skeleton-loader
                v-for="i in [1, 2, 3]"
                :key="i"
                class="mb-6"
            />
        </div>
        <div id="requests-container" v-else>
            <v-row class="mt-1">
                <v-col cols="12" md="auto" class="grow">
                    <v-text-field
                        v-model="searchQuery"
                        placeholder="Search by Reference, User, Email, Vehicle, Workshop"
                        prepend-inner-icon="mdi-magnify"
                        hide-details
                        outlined
                        dense
                    />
                </v-col>

                <v-col 
                    class="pl-0 shrink d-none d-sm-flex"
                >
                    <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                            <v-btn
                                rounded
                                color="orange darken-2"
                                small
                                fab
                                v-bind="attrs"
                                v-on="on"
                            >
                                {{ totalCount }}
                            </v-btn>
                        </template>
                        <span> Number of requests </span>
                    </v-tooltip>
                </v-col>
            </v-row>

            <v-row v-if="isOrganization">

                <v-col cols="12" md="auto" class="grow">
                    <v-menu
                        ref="monthFromFilterMenu"
                        v-model="monthFromFilterMenu"
                        :close-on-content-click="false"
                        :return-value.sync="monthFromFilterDate"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                    >
                        <template #activator="{ on, attrs }">
                            <v-text-field
                                :value="monthFromFilterDate"
                                label="From"
                                prepend-inner-icon="mdi-calendar"
                                hide-details
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"
                            />
                        </template>
                        
                        <v-date-picker
                            v-model="monthFromFilterDate"
                            type="month"
                            :max="maxMonth"
                            no-title
                            scrollable
                            @input="
                                $refs.monthFromFilterMenu.save(monthFromFilterDate);
                                monthFromFilterMenu = false;
                            "
                        />
                    </v-menu>
                </v-col>
                
                <v-col cols="12" md="auto" class="grow">
                    <v-menu
                        ref="monthToFilterMenu"
                        v-model="monthToFilterMenu"
                        :close-on-content-click="false"
                        :return-value.sync="monthToFilterDate"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                    >
                        <template #activator="{ on, attrs }">
                            <v-text-field
                                :value="monthToFilterDate"
                                label="To"
                                prepend-inner-icon="mdi-calendar"
                                hide-details
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="monthToFilterDate"
                            type="month"
                            :max="maxMonth"
                            no-title
                            scrollable
                            @input="
                                $refs.monthToFilterMenu.save(monthToFilterDate);
                                monthToFilterMenu = false;
                            "
                        />
                    </v-menu>
                </v-col>
            
                <v-col cols="12" md="auto" class="grow">
                    <v-text-field
                        v-model="workshopQuery"
                        placeholder="Workshop Name"
                        prepend-inner-icon="mdi-magnify"
                        hide-details
                        outlined
                        dense
                    />
                </v-col>
            
            </v-row>
            
            <div v-if="!loading && initialized && requests.length === 0" class="mt-5">
                <p class="mt-8">No records</p>
            </div>
            <div v-else class="mt-5">
                <active-request-tab-item
                    class="mb-6"
                    v-for="item in requests"
                    :key="item.id"
                    :enableCompleteButtonProp="stageProp == 4"
                    :quoteRequestProp="item"
                    :searchQueryProp="searchQueryList"
                    @resetRequests="resetRequests"
                />

                <scroll-pagination
                    ref="scrollPagination"
                    :loader-method-prop="searchRequests"
                    :loader-disable-prop="requests.length >= totalCount"
                />
            </div>
        </div>
    </v-sheet>
</template>

<script>
import _ from 'lodash';
import debounce from 'debounce';

import boxViewSkeletonLoader from '@/components/widgets/box-view-skeleton-loader';
import activeRequestTabItem from '@/components/active-request-tab-item';
import scrollPagination from '@/components/scroll-pagination';
import { quoteRequestService } from '@/services';

export default {
    name: 'active-request-tab',
    components: {
        boxViewSkeletonLoader,
        activeRequestTabItem,
        scrollPagination,
    },
    props: {
        stageProp: String,
    },
    data: () => ({
        searchQuery: '',
        workshopQuery: '',
        loading: true,
        initialized: false,

        tottalDisplayItems: 0,
        currentPage: 1,
        totalCount: 0,
        itemsPerPage: 20,
        
        workshops: [
            {
                name: 'All',
                id: 0,
                logoUrl: require('@/assets/images/smc-logo.png'),
            },
        ],
        
        maxMonth: new Date().toISOString().slice(0, 7),
        
        monthFromFilterMenu: false,
        monthFromFilterDate: null,
        
        monthToFilterMenu: false,
        monthToFilterDate: new Date().toISOString().slice(0, 7),
        
        requests: [],
        sortModel: {
            dir: 'desc',
            selectedSort: { text: 'Date added', value: 'dateCreatedUtc' },
        },
    }),
    computed: {
        isOrganization() {
            return this.$store.getters['user/isOrganization'];
        },
        searchQueryList() {
            let queryList = [];

            if (this.searchQuery) {
                queryList.push(this.searchQuery);
            }
            if (this.workshopQuery) {
                queryList.push(this.workshopQuery);
            }

            return queryList;
        },
    },
    async created() {
        this.searchRequests = debounce(this.searchRequests, 500);
        await this.searchRequests();
    },
    watch: {
        searchQuery: {
            handler: async function () {
                this.currentPage = 1;
                this.loading = true;
                this.requests = [];
                await this.searchRequests();
            },
        },
        workshopQuery: {
            handler: async function () {
                this.currentPage = 1;
                this.loading = true;
                this.requests = [];
                await this.searchRequests();
            },
        },
        monthFromFilterDate: {
            handler: async function (newValue) {
                this.currentPage = 1;
                this.loading = true;
                this.requests = [];
                
                const newDateFrom = new Date(newValue);
                const currentDateTo = new Date(this.monthToFilterDate);

                if (newDateFrom > currentDateTo) {
                    this.monthToFilterDate = newDateFrom.toISOString().slice(0, 7);
                }
                else {
                    await this.searchRequests();
                }
            },
        },
        monthToFilterDate: {
            handler: async function (newValue) {
                this.currentPage = 1;
                this.loading = true;
                this.requests = [];
                
                const currentDateFrom = new Date(this.monthFromFilterDate);
                const newDateTo = new Date(newValue);
                
                if (currentDateFrom > newDateTo) {
                    this.monthFromFilterDate = newDateTo.toISOString().slice(0, 7);
                }
                else {
                    await this.searchRequests();
                }
            },
        },
    },
    methods: {
        async resetRequests() {
            this.currentPage = 1;
            this.requests = [];
            await this.searchRequests();
        },
        async searchRequests() {
            this.loading = true;

            let headers = [
                {
                    value: 'workshopName',
                    filter: this.workshopQuery,
                },
            ];
            headers.push(
                {
                    value: 'monthFrom',
                    filter: this.monthFromFilterDate,
                },
                {
                    value: 'monthTo',
                    filter: this.monthToFilterDate,
                }
            );

            let result = await quoteRequestService.queryActiveRequests(
                this.stageProp,
                {
                    filter: _.trim(this.searchQuery),
                    page: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    headers: headers,
                },
            );
            this.requests.push(...result.items);
            this.totalCount = result.totalDisplayItems;
            this.currentPage++;
            this.loading = false;
            this.initialized = true;
        },
    },
};
</script>
